import { 
    ConnectClient, 
    ListInstancesCommand,
    ListInstanceAttributesCommand,
    ListInstanceStorageConfigsCommand,
    ListIntegrationAssociationsCommand,
    ListLambdaFunctionsCommand,
    ListBotsCommand,
    ListPhoneNumbersV2Command,
    ListFlowAssociationsCommand,
    ListPromptsCommand,
    ListRoutingProfilesCommand,
    ListRoutingProfileQueuesCommand,
    ListSecurityProfilesCommand,
    ListSecurityProfilePermissionsCommand,
    DescribeRoutingProfileCommand,
    DescribeContactFlowCommand,
    DescribeContactFlowModuleCommand,
    DescribeUserHierarchyStructureCommand,
    DescribeInstanceCommand,
    SearchQueuesCommand,
    SearchQuickConnectsCommand,
    SearchHoursOfOperationsCommand,
    SearchAgentStatusesCommand,
    SearchContactFlowsCommand,
    SearchContactFlowModulesCommand,
    SearchUserHierarchyGroupsCommand,
    SearchUsersCommand
} from '@aws-sdk/client-connect';

import { requestQueueManager } from './requestQueueManager';

const createConnectClient = (credentials, region) => {
  return new ConnectClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    retryMode: 'adaptive', // SDK adaptive retry adds a client-side rate limiter as a backstop
    maxAttempts: 2,        // bounded retry; RequestQueueManager's token bucket is the primary pacing
    requestHandler: {
      requestTimeout: 15000, // Reduced timeout to prevent hanging on rate limits
    }
  });
};;

// Helper: page through any Search* command (Search APIs return describe-level
// objects in pages of up to 100, eliminating the per-item Describe N+1 pattern).
// makeCommand(nextToken) returns the Search*Command; pluck(response) returns the array.
const fetchAllSearch = async (client, makeCommand, pluck, resourceType) => {
  const out = [];
  let nextToken;
  do {
    const token = nextToken;
    const response = await requestQueueManager.executeRequest(
      () => client.send(makeCommand(token)),
      { resourceType }
    );
    out.push(...(pluck(response) || []));
    nextToken = response.NextToken;
  } while (nextToken);
  return out;
};

// The full objects returned by the Search/Describe APIs use resource-prefixed identifier
// fields (QueueId/QueueArn, RoutingProfileId/RoutingProfileArn, ...), whereas the old List
// summaries — and every downstream mapper, lookup map, and import step — use plain Id/Arn.
// Add Id/Arn aliases so the rest of the pipeline is unchanged. Mutates and returns items.
const aliasIdArn = (items, idField, arnField) => {
  for (const item of items) {
    if (item.Id === undefined && item[idField] !== undefined) item.Id = item[idField];
    if (item.Arn === undefined && item[arnField] !== undefined) item.Arn = item[arnField];
  }
  return items;
};

// Helper function for batch processing with throttling
const processBatch = async (items, batchSize, processor, delayBetweenBatches = 0, options = {}) => {
  const { maxCircuitBreakerAttempts = 5 } = options;
  const results = [];
  let itemIndex = 0;
  let circuitBreakerAttempts = 0; // Track consecutive circuit breaker hits
  
  // Helper function to extract wait time from circuit breaker error messages
  const extractWaitTime = (errorMessage) => {
    if (!errorMessage) return 5000; // Default 5 seconds
    const waitTimeMatch = errorMessage.match(/Retry after (\d+) seconds/);
    return waitTimeMatch ? parseInt(waitTimeMatch[1]) * 1000 : 5000;
  };
  
  while (itemIndex < items.length) {
    const item = items[itemIndex];
    
    try {
      const result = await processor(item);
      if (result !== null && result !== undefined) {
        results.push(result);
      }
      itemIndex++; // Only increment on success
      circuitBreakerAttempts = 0; // Reset on success
      
    } catch (error) {
      if (requestQueueManager.isCircuitBreakerError(error)) {
        circuitBreakerAttempts++;
        
        // Check if we've exceeded max circuit breaker attempts
        if (circuitBreakerAttempts >= maxCircuitBreakerAttempts) {
          const remainingCount = items.length - itemIndex;
          console.error(`🛑 Circuit breaker limit reached (${maxCircuitBreakerAttempts} attempts). Skipping remaining ${remainingCount} items in this batch.`);
          
          // Log each skipped item
          for (let i = itemIndex; i < items.length; i++) {
            const skippedItem = items[i];
            const itemName = skippedItem?.Name || skippedItem?.name || skippedItem?.Id || `item ${i + 1}`;
            console.warn(`⏭️ Skipping "${itemName}" due to circuit breaker limit`);
          }
          
          // Return partial results - caller can continue with next resource type
          return results;
        }
        
        // Circuit breaker recovery - wait with jitter and retry SAME item
        const baseWaitTime = extractWaitTime(error.message);
        // Add random jitter (±20%) to prevent multiple items retrying simultaneously
        const jitter = baseWaitTime * 0.2 * (Math.random() - 0.5); // ±20% jitter
        const waitTime = Math.max(1000, baseWaitTime + jitter); // Minimum 1 second wait

        console.warn(`🚫 Circuit breaker triggered for item ${itemIndex + 1} (attempt ${circuitBreakerAttempts}/${maxCircuitBreakerAttempts}). Waiting ${Math.round(waitTime/1000)}s...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        console.log(`🔄 Retrying item ${itemIndex + 1} after circuit breaker recovery...`);
        // Don't increment itemIndex - retry same item by continuing while loop
        continue;
        
      } else {
        // Other errors - log and move to next item
        console.error(`Failed to process batch item:`, error);
        itemIndex++;
        circuitBreakerAttempts = 0; // Reset on non-circuit-breaker error
      }
    }
    
    // Add delay between batches if specified (every batchSize items)
    if (delayBetweenBatches > 0 && itemIndex % batchSize === 0 && itemIndex < items.length) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
    }
  }
  
  return results;
};

export const listConnectInstances = async (credentials, region) => {
  const client = createConnectClient(credentials, region);
  const allInstances = [];
  let nextToken = undefined;

  do {
    const currentToken = nextToken;
    const response = await requestQueueManager.executeRequest(
      async () => {
        const command = new ListInstancesCommand({
          MaxResults: 10,
          NextToken: currentToken,
        });
        return client.send(command);
      },
      { resourceType: 'instances', priority: 10 }
    );
    
    allInstances.push(...(response.InstanceSummaryList || []));
    nextToken = response.NextToken;
  } while (nextToken);

  return allInstances;
};

export const fetchInstanceConfig = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);
  const instanceConfig = {
    instanceId,
    region,
    fetchedAt: new Date().toISOString(),
  };

  // Fetch instance details
  try {
    const response = await requestQueueManager.executeRequest(
      async () => {
        const describeInstanceCommand = new DescribeInstanceCommand({
          InstanceId: instanceId,
        });
        return client.send(describeInstanceCommand);
      },
      { resourceType: 'instance-details' }
    );
    instanceConfig.instanceDetails = response.Instance;
  } catch (error) {
    console.error(`Failed to fetch instance details for ${instanceId}:`, error);
    instanceConfig.instanceDetailsError = error.message;
  }

  // Fetch instance attributes
  try {
    const response = await requestQueueManager.executeRequest(
      async () => {
        const attributesCommand = new ListInstanceAttributesCommand({
          InstanceId: instanceId,
        });
        return client.send(attributesCommand);
      },
      { resourceType: 'instance-attributes' }
    );
    instanceConfig.attributes = response.Attributes || [];
  } catch (error) {
    console.error(`Failed to fetch instance attributes for ${instanceId}:`, error);
    instanceConfig.attributesError = error.message;
  }

  // Fetch storage configurations
  try {
    const storageConfigs = [];
    let nextToken = undefined;

    // Fetch contact trace records first
    do {
      const currentToken = nextToken;
      const response = await requestQueueManager.executeRequest(
        async () => {
          const storageCommand = new ListInstanceStorageConfigsCommand({
            InstanceId: instanceId,
            ResourceType: 'CONTACT_TRACE_RECORDS',
            NextToken: currentToken,
          });
          return client.send(storageCommand);
        },
        { resourceType: 'storage-configs' }
      );
      storageConfigs.push(...(response.StorageConfigs || []));
      nextToken = response.NextToken;
    } while (nextToken);

    // Fetch other storage types with throttling
    const storageTypes = ['CALL_RECORDINGS', 'CHAT_TRANSCRIPTS', 'CONTACT_EVALUATIONS', 'SCHEDULED_REPORTS'];
    
    for (const resourceType of storageTypes) {
      try {
        nextToken = undefined;
        do {
          const currentToken = nextToken;
          const response = await requestQueueManager.executeRequest(
            async () => {
              const storageCommand = new ListInstanceStorageConfigsCommand({
                InstanceId: instanceId,
                ResourceType: resourceType,
                NextToken: currentToken,
              });
              return client.send(storageCommand);
            },
            { resourceType: 'storage-configs' }
          );
          storageConfigs.push(...(response.StorageConfigs || []));
          nextToken = response.NextToken;
        } while (nextToken);
      } catch (storageError) {
        console.warn(`Failed to fetch ${resourceType} storage config for ${instanceId}:`, storageError.message);
      }
    }

    instanceConfig.storageConfigs = storageConfigs;
  } catch (error) {
    console.error(`Failed to fetch instance storage configs for ${instanceId}:`, error);
    instanceConfig.storageConfigsError = error.message;
  }

  // Fetch integration associations
  try {
    const integrations = [];
    let nextToken = undefined;

    do {
      const currentToken = nextToken;
      const response = await requestQueueManager.executeRequest(
        async () => {
          const integrationsCommand = new ListIntegrationAssociationsCommand({
            InstanceId: instanceId,
            NextToken: currentToken,
          });
          return client.send(integrationsCommand);
        },
        { resourceType: 'integrations' }
      );
      integrations.push(...(response.IntegrationAssociationSummaryList || []));
      nextToken = response.NextToken;
    } while (nextToken);

    instanceConfig.integrationAssociations = integrations;
  } catch (error) {
    console.error(`Failed to fetch integration associations for ${instanceId}:`, error);
    instanceConfig.integrationAssociationsError = error.message;
  }

  // Fetch Lambda functions
  try {
    const lambdaFunctions = [];
    let nextToken = undefined;

    do {
      const currentToken = nextToken;
      const response = await requestQueueManager.executeRequest(
        async () => {
          const lambdaCommand = new ListLambdaFunctionsCommand({
            InstanceId: instanceId,
            NextToken: currentToken,
          });
          return client.send(lambdaCommand);
        },
        { resourceType: 'lambda-functions' }
      );
      lambdaFunctions.push(...(response.LambdaFunctions || []));
      nextToken = response.NextToken;
    } while (nextToken);

    instanceConfig.lambdaFunctions = lambdaFunctions;
  } catch (error) {
    console.error(`Failed to fetch Lambda functions for ${instanceId}:`, error);
    instanceConfig.lambdaFunctionsError = error.message;
  }

  // Fetch Lex bots
  try {
    const lexBots = [];
    
    // Fetch V2 bots
    try {
      let nextToken = undefined;
      do {
        const currentToken = nextToken;
        const response = await requestQueueManager.executeRequest(
          async () => {
            const lexV2Command = new ListBotsCommand({
              InstanceId: instanceId,
              NextToken: currentToken,
              LexVersion: 'V2'
            });
            return client.send(lexV2Command);
          },
          { resourceType: 'lex-bots-v2' }
        );
        lexBots.push(...(response.LexBots || []));
        nextToken = response.NextToken;
      } while (nextToken);
    } catch (v2Error) {
      console.error(`Failed to fetch Lex V2 bots for ${instanceId}:`, v2Error);
    }

    instanceConfig.lexBots = lexBots;
  } catch (error) {
    console.error(`Failed to fetch Lex bots for ${instanceId}:`, error);
    instanceConfig.lexBotsError = error.message;
  }

  return instanceConfig;
};

export const fetchPhoneNumbers = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);
  const phoneNumbers = [];
  let nextToken;

  // Fetch all phone numbers
  do {
    const currentToken = nextToken;
    const response = await requestQueueManager.executeRequest(
      async () => {
        const command = new ListPhoneNumbersV2Command({
          MaxResults: 1000,
          InstanceId: instanceId,
          NextToken: currentToken,
        });
        return client.send(command);
      },
      { resourceType: 'phone-numbers' }
    );
    phoneNumbers.push(...(response.ListPhoneNumbersSummaryList || []));
    nextToken = response.NextToken;
  } while (nextToken);

  // Fetch flow associations
  const flowMap = new Map();
  nextToken = undefined;

  do {
    const currentToken = nextToken;
    const response = await requestQueueManager.executeRequest(
      async () => {
        const command = new ListFlowAssociationsCommand({
          InstanceId: instanceId,
          ResourceType: 'VOICE_PHONE_NUMBER',
          NextToken: currentToken,
        });
        return client.send(command);
      },
      { resourceType: 'flow-associations' }
    );

    const associations = response?.FlowAssociationSummaryList || [];
    associations.forEach((assoc) => {
      flowMap.set(assoc.ResourceId, assoc.FlowId);
    });

    nextToken = response.NextToken;
  } while (nextToken);

  // Attach FlowId to each phone number
  for (const phone of phoneNumbers) {
    phone.FlowId = flowMap.get(phone.PhoneNumberArn) || null;
  }

  return phoneNumbers;
};

export const fetchHoursOfOperation = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  // SearchHoursOfOperations returns full HoursOfOperation objects (incl. Config) in
  // pages of up to 100 — no per-item DescribeHoursOfOperation needed.
  const hoursOfOperation = await fetchAllSearch(
    client,
    (nextToken) => new SearchHoursOfOperationsCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.HoursOfOperations,
    'hoursOfOperation'
  );

  return aliasIdArn(hoursOfOperation, 'HoursOfOperationId', 'HoursOfOperationArn');
};

export const fetchPrompts = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);
  const prompts = [];
  let nextToken;

  do {
    const currentToken = nextToken;
    const response = await requestQueueManager.executeRequest(
      async () => {
        const command = new ListPromptsCommand({
          MaxResults: 1000,
          InstanceId: instanceId,
          NextToken: currentToken,
        });
        return client.send(command);
      },
      { resourceType: 'prompts' }
    );
    prompts.push(...(response.PromptSummaryList || []));
    nextToken = response.NextToken;
  } while (nextToken);

  return prompts;
};

export const fetchQueues = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  // SearchQueues returns full Queue objects in pages of up to 100 — no per-item
  // DescribeQueue needed. The Queue object has no QueueType field, so the STANDARD-only
  // restriction (formerly ListQueues({ QueueTypes: ['STANDARD'] })) must be applied
  // server-side via SearchCriteria.QueueTypeCondition, not client-side.
  const queues = await fetchAllSearch(
    client,
    (nextToken) => new SearchQueuesCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
      SearchCriteria: { QueueTypeCondition: 'STANDARD' },
    }),
    (response) => response.Queues,
    'queues'
  );

  // Queue objects from Search omit QueueType; synthesize it so the Excel "type" column
  // (mapQueues) stays populated — SearchQueues only ever returns STANDARD queues.
  for (const queue of queues) {
    if (queue.QueueType === undefined) queue.QueueType = 'STANDARD';
  }
  return aliasIdArn(queues, 'QueueId', 'QueueArn');
};

export const fetchQuickConnects = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  // SearchQuickConnects returns full QuickConnect objects (incl. QuickConnectConfig)
  // in pages of up to 100 — no per-item DescribeQuickConnect needed.
  const quickConnects = await fetchAllSearch(
    client,
    (nextToken) => new SearchQuickConnectsCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.QuickConnects,
    'quickConnects'
  );

  // Search nests the type at QuickConnectConfig.QuickConnectType; the old ListQuickConnects
  // summary exposed it at top level, which mapQuickConnects reads. Surface it back up.
  for (const qc of quickConnects) {
    if (qc.QuickConnectType === undefined) qc.QuickConnectType = qc.QuickConnectConfig?.QuickConnectType;
  }
  return aliasIdArn(quickConnects, 'QuickConnectId', 'QuickConnectARN');
};

export const fetchRoutingProfiles = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);
  const routingProfiles = [];
  let nextToken;

  // SearchRoutingProfiles does NOT populate MediaConcurrencies.CrossChannelBehavior or
  // AgentAvailabilityTimer (per the AWS API Reference), both of which the export needs.
  // Routing profiles already require a per-profile call (ListRoutingProfileQueues) for the
  // associated-queue list, so this resource stays on List + DescribeRoutingProfile, which
  // returns the full object, rather than Search.
  do {
    const currentToken = nextToken;
    const response = await requestQueueManager.executeRequest(
      async () => {
        const command = new ListRoutingProfilesCommand({
          MaxResults: 1000,
          InstanceId: instanceId,
          NextToken: currentToken,
        });
        return client.send(command);
      },
      { resourceType: 'routingProfiles' }
    );
    routingProfiles.push(...(response.RoutingProfileSummaryList || []));
    nextToken = response.NextToken;
  } while (nextToken);

  // Per profile: DescribeRoutingProfile (full config incl. CrossChannelBehavior +
  // AgentAvailabilityTimer) then ListRoutingProfileQueues (associated queues).
  let routingProfileIndex = 0;
  while (routingProfileIndex < routingProfiles.length) {
    const routingProfile = routingProfiles[routingProfileIndex];

    try {
      // Skip if already enriched (for any potential retries)
      if (routingProfile.Description || routingProfile.RoutingProfileQueueConfigSummaryList) {
        routingProfileIndex++;
        continue;
      }

      // Describe routing profile (API call 1 of 2)
      try {
        const describeResponse = await requestQueueManager.executeRequest(
          async () => {
            const describeCommand = new DescribeRoutingProfileCommand({
              InstanceId: instanceId,
              RoutingProfileId: routingProfile.Id,
            });
            return client.send(describeCommand);
          },
          { resourceType: 'routingProfiles' }
        );

        const detailedRoutingProfile = describeResponse.RoutingProfile;
        for (const key in detailedRoutingProfile) {
          if (!routingProfile.hasOwnProperty(key)) {
            routingProfile[key] = detailedRoutingProfile[key];
          }
        }
      } catch (describeError) {
        if (requestQueueManager.isCircuitBreakerError(describeError)) {
          throw describeError;
        }
        console.error(`Failed to describe routing profile ${routingProfile.Name || routingProfile.Id}:`, describeError);
        routingProfile.DescribeError = describeError.message;
      }

      // Get queue associations (API call 2 of 2)
      try {
        const queueResponse = await requestQueueManager.executeRequest(
          async () => {
            const listCommand = new ListRoutingProfileQueuesCommand({
              InstanceId: instanceId,
              RoutingProfileId: routingProfile.Id,
            });
            return client.send(listCommand);
          },
          { resourceType: 'routing-profile-queues' }
        );
        
        routingProfile['RoutingProfileQueueConfigSummaryList'] = 
          queueResponse.RoutingProfileQueueConfigSummaryList;
      } catch (queueError) {
        // Re-throw circuit breaker errors so they can trigger proper recovery
        if (requestQueueManager.isCircuitBreakerError(queueError)) {
          throw queueError;
        }
        
        console.error(`Failed to get queue associations for routing profile ${routingProfile.Name || routingProfile.Id}:`, queueError);
        routingProfile.QueueAssociationError = queueError.message;
      }

      // Successfully processed this routing profile, move to next
      routingProfileIndex++;
      
    } catch (error) {
      // Handle circuit breaker errors with proper recovery
      if (requestQueueManager.isCircuitBreakerError(error)) {
        console.warn(`🚫 Circuit breaker triggered while processing routing profile ${routingProfile.Name || routingProfile.Id}. Waiting for recovery...`);
        
        // Extract wait time from error message (format: "Retry after X seconds.")
        const waitTimeMatch = error.message.match(/Retry after (\d+) seconds/);
        const waitTimeSeconds = waitTimeMatch ? parseInt(waitTimeMatch[1]) : 30;
        const waitTimeMs = waitTimeSeconds * 1000;
        
        console.log(`⏳ Waiting ${waitTimeSeconds} seconds for circuit breaker recovery...`);
        await requestQueueManager.delay(waitTimeMs);
        
        // Don't increment index - retry the same routing profile
        console.log(`🔄 Retrying routing profile ${routingProfile.Name || routingProfile.Id} after circuit breaker recovery`);
        continue;
      }
      
      console.error(`Failed to process routing profile ${routingProfile.Name || routingProfile.Id}:`, error);
      routingProfile.ProcessingError = error.message;
      // Continue with next routing profile
      routingProfileIndex++;
    }
  }

  console.log(`✅ Successfully processed ${routingProfiles.length} routing profiles for instance ${instanceId}`);
  return routingProfiles;
};;;;;;;;

export const fetchAgentStatus = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  // SearchAgentStatuses returns full AgentStatus objects in pages of up to 100 —
  // no per-item DescribeAgentStatus needed.
  const agentStatuses = await fetchAllSearch(
    client,
    (nextToken) => new SearchAgentStatusesCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.AgentStatuses,
    'agentStatuses'
  );

  return aliasIdArn(agentStatuses, 'AgentStatusId', 'AgentStatusARN');
};

export const fetchSecurityProfiles = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);
  const securityProfiles = [];
  let nextToken;

  // List all security profiles
  do {
    const currentToken = nextToken;
    const response = await requestQueueManager.executeRequest(
      async () => {
        const command = new ListSecurityProfilesCommand({
          MaxResults: 1000,
          InstanceId: instanceId,
          NextToken: currentToken,
        });
        return client.send(command);
      },
      { resourceType: 'security-profiles-list' }
    );
    securityProfiles.push(...(response.SecurityProfileSummaryList || []));
    nextToken = response.NextToken;
  } while (nextToken);

  // The ListSecurityProfiles summary already provides Name/Description/Arn (everything the
  // export consumes except Permissions), so DescribeSecurityProfile is redundant and is
  // dropped. Permissions have no bulk source, so ListSecurityProfilePermissions is still
  // called per profile.
  await processBatch(
    securityProfiles,
    5, // Process 5 profiles at a time
    async (profile) => {
      try {
        const permissionsResponse = await requestQueueManager.executeRequest(
          async () => {
            const listCommand = new ListSecurityProfilePermissionsCommand({
              InstanceId: instanceId,
              SecurityProfileId: profile.Id,
            });
            return client.send(listCommand);
          },
          { resourceType: 'securityProfiles' }
        );

        profile['Permissions'] = permissionsResponse.Permissions;

      } catch (error) {
        console.error(`Failed to fetch permissions for security profile ${profile.Name}:`, error);
      }
    }
  );

  return securityProfiles;
};

export const fetchUserHierarchyGroups = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  // SearchUserHierarchyGroups returns full HierarchyGroup objects (incl. HierarchyPath)
  // in pages of up to 100 — no per-group DescribeUserHierarchyGroup needed.
  const userHierarchyGroups = await fetchAllSearch(
    client,
    (nextToken) => new SearchUserHierarchyGroupsCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.UserHierarchyGroups,
    'userHierarchyGroups'
  );

  // Fetch the hierarchy structure
  const structureResponse = await requestQueueManager.executeRequest(
    async () => {
      const command = new DescribeUserHierarchyStructureCommand({
        InstanceId: instanceId,
      });
      return client.send(command);
    },
    { resourceType: 'hierarchy-structure' }
  );

  const hierarchyStructure = structureResponse.HierarchyStructure;

  return {
    userHierarchyGroups,
    hierarchyStructure,
  };
};

export const fetchContactFlows = async (credentials, region, instanceId, options = {}) => {
  const client = createConnectClient(credentials, region);

  // SearchContactFlows enumerates all flows in pages of up to 100 (replacing the slow
  // ListContactFlows + per-flow Describe enumeration). It returns Type/State/Status but
  // NOT Content (verified live: Content comes back null), so Content is backfilled below
  // only when diagrams are requested.
  const contactFlows = await fetchAllSearch(
    client,
    (nextToken) => new SearchContactFlowsCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.ContactFlows,
    'contactFlows'
  );

  // Search uses Type/State/Status; the Excel/diagram/summarization consumers read the
  // List/Describe-era names. Alias them so those paths work unchanged.
  for (const flow of contactFlows) {
    if (flow.ContactFlowType === undefined) flow.ContactFlowType = flow.Type;
    if (flow.ContactFlowState === undefined) flow.ContactFlowState = flow.State;
    if (flow.ContactFlowStatus === undefined) flow.ContactFlowStatus = flow.Status;
  }

  // Diagrams and AI summaries need the full flow JSON, which SearchContactFlows omits.
  // Backfill Content via DescribeContactFlow for any flow that lacks it, batched through the
  // rate limiter (faster + safer than the old sequential one-at-a-time + 5s-cooldown path).
  if (options.generateDiagrams) {
    const flowsNeedingContent = contactFlows.filter(flow => !flow.Content);
    await processBatch(
      flowsNeedingContent,
      5,
      async (flow) => {
        try {
          const response = await requestQueueManager.executeRequest(
            async () => {
              const describeCommand = new DescribeContactFlowCommand({
                InstanceId: instanceId,
                ContactFlowId: flow.Id,
              });
              return client.send(describeCommand);
            },
            { resourceType: 'contactFlows' }
          );
          if (response.ContactFlow?.Content) flow.Content = response.ContactFlow.Content;
        } catch (error) {
          if (requestQueueManager.isCircuitBreakerError(error)) throw error;
          console.error(`Failed to fetch content for flow ${flow.Name || flow.Id}:`, error);
          flow.ContentError = error.message;
        }
      }
    );
  }

  // Initialize error metrics to 0 - will be populated by unified error metrics function
  contactFlows.forEach(flow => {
    flow.ContactFlowErrors30Days = 0;
  });

  return contactFlows;
};

export const fetchContactFlowModules = async (credentials, region, instanceId, options = {}) => {
  const client = createConnectClient(credentials, region);

  // SearchContactFlowModules enumerates modules in pages of up to 100. Like SearchContactFlows
  // it returns State/Status but NOT Content (verified live: Content is null), so Content is
  // backfilled below only when diagrams are requested.
  const contactFlowModules = await fetchAllSearch(
    client,
    (nextToken) => new SearchContactFlowModulesCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.ContactFlowModules,
    'contactFlowModules'
  );

  // Alias Search's State/Status to the names the consumers read.
  for (const module of contactFlowModules) {
    if (module.ContactFlowModuleState === undefined) module.ContactFlowModuleState = module.State;
    if (module.ContactFlowModuleStatus === undefined) module.ContactFlowModuleStatus = module.Status;
  }

  // Backfill module Content via DescribeContactFlowModule when diagrams are requested.
  if (options.generateDiagrams) {
    const modulesNeedingContent = contactFlowModules.filter(module => !module.Content);
    await processBatch(
      modulesNeedingContent,
      5,
      async (module) => {
        try {
          const response = await requestQueueManager.executeRequest(
            async () => {
              const describeCommand = new DescribeContactFlowModuleCommand({
                InstanceId: instanceId,
                ContactFlowModuleId: module.Id,
              });
              return client.send(describeCommand);
            },
            { resourceType: 'contactFlowModules' }
          );
          if (response.ContactFlowModule?.Content) module.Content = response.ContactFlowModule.Content;
        } catch (error) {
          if (requestQueueManager.isCircuitBreakerError(error)) throw error;
          console.error(`Failed to fetch content for module ${module.Name || module.Id}:`, error);
          module.ContentError = error.message;
        }
      }
    );
  }

  // Initialize error metrics to 0 - will be populated by unified error metrics function
  contactFlowModules.forEach(module => {
    module.ContactFlowErrors30Days = 0;
  });

  return contactFlowModules;
};

export const fetchUsers = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  // SearchUsers returns UserSearchSummary objects in pages of up to 100 — replacing the
  // previous ListUsers + one-at-a-time DescribeUser path (the slowest in the export, capped
  // at 1.5 req/s). It carries everything the export consumes — RoutingProfileId,
  // SecurityProfileIds, HierarchyGroupId, PhoneConfig, Username, and IdentityInfo
  // (FirstName/LastName) — EXCEPT Email: SearchUsers returns a "lite" identity with no
  // email address, so the export's Email column is intentionally left blank.
  const users = await fetchAllSearch(
    client,
    (nextToken) => new SearchUsersCommand({
      InstanceId: instanceId,
      MaxResults: 100,
      NextToken: nextToken,
    }),
    (response) => response.Users,
    'users'
  );

  // SearchUsers returns PhoneConfig.AfterContactWorkTimeLimit in MILLISECONDS, whereas
  // DescribeUser (the old path) returned SECONDS — and the export's ACW Timeout column
  // expects seconds. Convert back so e.g. 90000 -> 90.
  for (const user of users) {
    const acw = user.PhoneConfig?.AfterContactWorkTimeLimit;
    if (typeof acw === 'number' && acw > 0) {
      user.PhoneConfig.AfterContactWorkTimeLimit = Math.round(acw / 1000);
    }
  }

  return users;
};